import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { BackupDTO, JobDTO, TargetDTO } from '@devrobot/shared';
import { DatabaseBackup, Info, RotateCcw, TriangleAlert } from 'lucide-react';
import { api, seg } from '../../api/client';
import { qk, useApiMutation, useBackups } from '../../api/queries';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { ErrorAlert } from '../../components/ErrorAlert';
import { KeyValue } from '../../components/KeyValue';
import { RelativeTime } from '../../components/RelativeTime';
import { SkeletonLines, SkeletonTable } from '../../components/Skeleton';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toaster';
import { Haptic } from '../../kit/haptics';
import { haptic } from '../../kit/native';
import { formatBytes } from '../../lib/format';

interface RestorePreview {
  backup: BackupDTO;
  target: string;
  impact: string;
  confirmPhrase: string;
}

function RestoreDialog({ backup, onClose }: { backup: BackupDTO; onClose: () => void }) {
  const navigate = useNavigate();
  const preview = useQuery({ queryKey: ['restore-preview', backup.id], queryFn: () => api.get<RestorePreview>(`/api/v1/backups/${seg(backup.id)}/restore-preview`), staleTime: 0, retry: false });
  const restore = useApiMutation((confirm: string) => api.post<{ job: JobDTO }>(`/api/v1/backups/${seg(backup.id)}/restore`, { confirm }), { invalidate: [qk.backups(backup.targetId), ['jobs']] });

  if (preview.isPending || preview.isError) {
    return (
      <ConfirmDialog title="Restore from backup" confirmLabel="Restore" tone="error" confirmDisabled onConfirm={() => {}} onCancel={onClose} error={preview.error}>
        {preview.isPending ? <SkeletonLines lines={4} /> : null}
      </ConfirmDialog>
    );
  }
  const p = preview.data;
  return (
    <ConfirmDialog
      title="Restore from backup"
      confirmLabel="Replace the data"
      tone="error"
      size="lg"
      typedConfirmation={p.confirmPhrase}
      busy={restore.isPending}
      error={restore.error}
      onCancel={onClose}
      onConfirm={() => restore.mutate(p.confirmPhrase, { onSuccess: (r) => navigate(`/jobs/${r.job.id}`), onError: () => haptic('error') })}
    >
      <KeyValue
        items={[
          { label: 'Target', value: <span className="font-mono">{p.target}</span> },
          { label: 'Database', value: `${p.backup.database.service} (${p.backup.database.type})` },
          { label: 'Backup taken', value: <RelativeTime value={p.backup.createdAt} /> },
          { label: 'Size', value: formatBytes(p.backup.sizeBytes) },
          { label: 'Checksum', value: p.backup.sha256 ? <span className="font-mono text-xs">{p.backup.sha256.slice(0, 16)}…</span> : '—' },
        ]}
      />
      <div role="alert" className="alert alert-error alert-soft text-sm">
        <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
        <span>{p.impact}</span>
      </div>
      <p className="text-sm text-base-content/65">If the restore fails partway, the database may be partially changed; the job will say so. Consider taking a fresh backup first.</p>
    </ConfirmDialog>
  );
}

function BackupActions({ b, canRestore, busy, onRestore }: { b: BackupDTO; canRestore: boolean; busy: boolean; onRestore: (b: BackupDTO) => void }) {
  if (!b.jobId && !(canRestore && b.state === 'verified')) return null;
  return (
    <span className="inline-flex items-center gap-1">
      {b.jobId ? (
        <Link className="btn btn-ghost btn-xs" to={`/jobs/${b.jobId}`}>
          Job
        </Link>
      ) : null}
      {canRestore && b.state === 'verified' ? (
        <button type="button" className="btn btn-ghost btn-xs dr-ink-error" onClick={() => onRestore(b)} disabled={busy}>
          <RotateCcw className="size-3.5" aria-hidden="true" /> Restore
        </button>
      ) : null}
    </span>
  );
}

export function BackupsTab({ target: t }: { target: TargetDTO }) {
  const toast = useToast();
  const navigate = useNavigate();
  const q = useBackups(t.id);
  const dbs = t.databases.filter((d) => d.backupSupported);
  const unsupported = t.databases.filter((d) => !d.backupSupported);
  const [database, setDatabase] = useState(dbs[0]?.service ?? '');
  const [restoring, setRestoring] = useState<BackupDTO | null>(null);
  const create = useApiMutation((db: string) => api.post<{ job: JobDTO; backupId: string }>(`/api/v1/targets/${seg(t.id)}/backups`, { database: db || undefined }), { invalidate: [qk.backups(t.id), ['jobs']] });
  const deployed = !t.removedAt && t.state.process !== 'not-deployed';

  return (
    <div className="space-y-4">
      <p className="text-sm text-base-content/65">
        A backup is marked <strong>verified</strong> only when the export and every transfer step succeeded; only verified backups can be restored. Artifacts are stored by the DevRobot control plane, outside this VPS.
      </p>
      {t.databases.length === 0 ? (
        <EmptyState icon={DatabaseBackup} title="No database declared">
          Declare databases under <code className="font-mono">persistence.databases</code> in devrobot.yaml to enable backups. Application file volumes are not covered by database backups.
        </EmptyState>
      ) : null}
      {unsupported.length > 0 ? (
        <div role="note" className="alert alert-info alert-soft text-sm">
          <Info className="size-4 shrink-0" aria-hidden="true" />
          <span>
            No supported backup adapter for {unsupported.map((d) => `${d.service} (${d.type})`).join(', ')}. PostgreSQL is currently supported.
          </span>
        </div>
      ) : null}
      {t.can.backup && dbs.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          {dbs.length > 1 ? (
            <select className="select select-sm w-auto" value={database} onChange={(e) => setDatabase(e.target.value)} aria-label="Database">
              {dbs.map((d) => (
                <option key={d.service} value={d.service}>
                  {d.service} ({d.type})
                </option>
              ))}
            </select>
          ) : null}
          <Haptic disabled={!deployed || create.isPending || Boolean(t.activeJob)}>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={!deployed || create.isPending || Boolean(t.activeJob)}
              onClick={() =>
                create.mutate(database, {
                  onSuccess: (r) =>
                    toast.success('Backup started', {
                      description: `Exporting ${database || dbs[0]?.service || 'the database'}. It is marked verified once every transfer step succeeds.`,
                      action: { label: 'View job', onClick: () => navigate(`/jobs/${r.job.id}`) },
                    }),
                  onError: () => haptic('error'),
                })
              }
            >
              {create.isPending ? <span className="loading loading-spinner loading-sm" aria-hidden="true" /> : <DatabaseBackup className="size-4" aria-hidden="true" />}
              Back up now
            </button>
          </Haptic>
          {!deployed ? <span className="text-sm text-base-content/60">Backups need a running deployment.</span> : null}
        </div>
      ) : null}
      <ErrorAlert error={create.error} title="The backup was not started" />
      {q.isPending ? <SkeletonTable rows={3} /> : null}
      {q.isError ? <ErrorAlert error={q.error} onRetry={() => void q.refetch()} /> : null}
      {q.data && q.data.length === 0 && t.databases.length > 0 ? <p className="text-sm text-base-content/60">No backups yet.</p> : null}
      {q.data && q.data.length > 0 ? (
        <div className="card dr-surface overflow-hidden">
          {/* Phones: stacked backup rows instead of a seven-column table. */}
          <ul className="divide-y divide-hairline md:hidden">
            {q.data.map((b) => (
              <li key={b.id} className="space-y-1.5 px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium">
                    {b.database.service} <span className="font-normal text-base-content/60">({b.database.type})</span>
                  </span>
                  <StatusBadge kind="backup" value={b.state} size="xs" />
                </div>
                <p className="text-xs text-base-content/60">
                  <RelativeTime value={b.createdAt} /> · by {b.createdBy} · {formatBytes(b.sizeBytes)} · {b.storage === 'control-plane' ? 'control plane' : 'not stored'}
                </p>
                {b.verifiedAt ? (
                  <p className="text-xs text-base-content/60">
                    verified <RelativeTime value={b.verifiedAt} />
                  </p>
                ) : null}
                {b.sha256 ? (
                  <p className="truncate font-mono text-xs text-base-content/55" title={b.sha256}>
                    {b.sha256.slice(0, 12)}…
                  </p>
                ) : null}
                {b.error ? <p className="text-xs break-anywhere dr-ink-error">{b.error}</p> : null}
                <BackupActions b={b} canRestore={t.can.restore} busy={Boolean(t.activeJob)} onRestore={setRestoring} />
              </li>
            ))}
          </ul>
          <div className="overflow-x-auto max-md:hidden">
            <table className="table table-sm">
              <thead>
                <tr className="text-xs tracking-wide text-base-content/50 uppercase">
                  <th>Taken</th>
                  <th>Database</th>
                  <th>State</th>
                  <th>Size</th>
                  <th className="max-lg:hidden">Checksum</th>
                  <th className="max-lg:hidden">Stored</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((b) => (
                  <tr key={b.id} className="hover:bg-base-200/50">
                    <td className="text-sm whitespace-nowrap">
                      <RelativeTime value={b.createdAt} />
                      <div className="text-xs text-base-content/55">by {b.createdBy}</div>
                    </td>
                    <td className="text-sm">
                      {b.database.service} <span className="text-base-content/60">({b.database.type})</span>
                    </td>
                    <td>
                      <StatusBadge kind="backup" value={b.state} size="xs" />
                      {b.verifiedAt ? (
                        <div className="text-xs text-base-content/55">
                          verified <RelativeTime value={b.verifiedAt} />
                        </div>
                      ) : null}
                      {b.error ? <div className="max-w-xs text-xs break-anywhere dr-ink-error">{b.error}</div> : null}
                    </td>
                    <td className="text-sm whitespace-nowrap tabular-nums">{formatBytes(b.sizeBytes)}</td>
                    <td className="font-mono text-xs max-lg:hidden" title={b.sha256}>
                      {b.sha256 ? `${b.sha256.slice(0, 12)}…` : '—'}
                    </td>
                    <td className="text-xs text-base-content/65 max-lg:hidden">{b.storage === 'control-plane' ? 'control plane' : 'not stored'}</td>
                    <td className="text-right whitespace-nowrap">
                      <BackupActions b={b} canRestore={t.can.restore} busy={Boolean(t.activeJob)} onRestore={setRestoring} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      {restoring ? <RestoreDialog backup={restoring} onClose={() => setRestoring(null)} /> : null}
    </div>
  );
}
